import { useState, useCallback, useRef, useEffect } from 'react';
import { useSpeechSynthesis } from 'react-speech-kit';
import apiService from '../services/apiService';

export const useSmartTTS = () => {
    // Track the onEnd callback for the current speech
    const onEndRef = useRef(null);

    // 1. Basic TTS (Browser Native)
    const { speak: webSpeak, speaking: webSpeaking, cancel: webCancel, voices } = useSpeechSynthesis({
        onEnd: () => {
            if (onEndRef.current) {
                onEndRef.current();
                onEndRef.current = null;
            }
        },
    });

    // 2. Premium TTS (Server OpenAI)
    const [serverSpeaking, setServerSpeaking] = useState(false);
    const audioRef = useRef(null);
    const [currentUrl, setCurrentUrl] = useState(null);

    // Cleanup URL
    useEffect(() => {
        return () => {
            if (currentUrl) URL.revokeObjectURL(currentUrl);
        };
    }, [currentUrl]);

    const speak = useCallback(async ({ text, voice = 'alloy', speed = 1.0, type = 'auto', audioUrl = null, onProgress, onEnd }) => {
        if (!text) return;

        // Store onEnd callback
        onEndRef.current = onEnd;

        // Determine mode
        let mode = 'premium';
        if (type === 'word') {
            mode = 'basic';
        } else if (type === 'sentence') {
            mode = 'premium';
        } else {
            mode = text.length < 60 ? 'basic' : 'premium';
        }

        if (audioUrl) mode = 'premium';

        if (mode === 'basic') {
            const enVoice = voices.find(v => v.lang.includes('en-US')) || voices.find(v => v.lang.includes('en'));
            webSpeak({ text, rate: speed || 0.9, voice: enVoice || null });
        } else {
            try {
                if (audioRef.current) {
                    audioRef.current.pause();
                    audioRef.current.currentTime = 0;
                }
                webCancel();

                setServerSpeaking(true);

                let url = audioUrl;

                if (!url) {
                    const result = await apiService.generateAudio(text, voice, speed);
                    if (typeof result === 'string') {
                        url = result;
                    } else {
                        url = URL.createObjectURL(result);
                    }
                }

                if (currentUrl && currentUrl !== audioUrl) {
                    URL.revokeObjectURL(currentUrl);
                }

                if (!audioUrl) setCurrentUrl(url);

                const audio = new Audio(url);
                audioRef.current = audio;

                audio.ontimeupdate = () => {
                    if (onProgress && audio.duration) {
                        onProgress(audio.currentTime, audio.duration);
                    }
                };

                audio.onended = () => {
                    setServerSpeaking(false);
                    // Call the stored onEndRef (which matches the passed onEnd)
                    if (onEndRef.current) {
                        onEndRef.current();
                        onEndRef.current = null;
                    }
                };

                audio.onerror = (e) => {
                    console.error("Audio playback error", e);
                    setServerSpeaking(false);
                    if (onEndRef.current) {
                        onEndRef.current(); // Ensure cleanup even on error
                        onEndRef.current = null;
                    }
                };

                await audio.play();

            } catch (error) {
                console.error("Server TTS Error:", error);
                setServerSpeaking(false);
                // Fallback to basic
                webSpeak({ text, rate: speed });
            }
        }
    }, [voices, webSpeak, webCancel, currentUrl]);

    const cancel = useCallback(() => {
        webCancel();
        if (audioRef.current) {
            audioRef.current.pause();
            setServerSpeaking(false);
        }
        if (onEndRef.current) {
            onEndRef.current();
            onEndRef.current = null;
        }
    }, [webCancel]);

    return {
        speak,
        speaking: webSpeaking || serverSpeaking,
        cancel,
        supported: true,
        voices
    };
};
